/**
 * Wiederholungsregeln (SPEC 9.4).
 *
 * Eine Teilmenge von RFC 5545 — bewusst benannt und bewusst klein: `FREQ`,
 * `INTERVAL`, `BYDAY`, `BYMONTHDAY`. Mehr braucht ein Haushaltsplan nicht, und
 * jede weitere Regel waere eine, die irgendwann falsch rechnet, ohne dass es
 * jemand merkt.
 *
 * Gerechnet wird durchgaengig ueber lokale Datumsteile, nie ueber
 * Millisekunden. Der Unterschied faellt genau zweimal im Jahr auf: „jeden
 * Montag um 8 Uhr" muss ueber die Zeitumstellung hinweg 8 Uhr bleiben, und
 * `+ 7 * 86_400_000` macht daraus 7 oder 9 Uhr.
 */
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
export const FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'];
/** Montag = 0, damit es zur ISO-Woche passt (SPEC 16). */
function isoWeekday(date) {
    return (date.getDay() + 6) % 7;
}
function daysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}
/** Neues Datum mit der Tageszeit der Vorlage. */
function at(reference, year, month, day) {
    return new Date(year, month, day, reference.getHours(), reference.getMinutes(), reference.getSeconds(), reference.getMilliseconds());
}
function addDays(reference, days) {
    return at(reference, reference.getFullYear(), reference.getMonth(), reference.getDate() + days);
}
export function parseRrule(value) {
    const parts = new Map();
    for (const chunk of value.split(';')) {
        const [key, raw] = chunk.split('=');
        if (!key || raw === undefined)
            continue;
        parts.set(key.trim().toUpperCase(), raw.trim().toUpperCase());
    }
    const freq = parts.get('FREQ');
    if (!freq || !FREQUENCIES.includes(freq))
        return null;
    const interval = parts.has('INTERVAL') ? Number(parts.get('INTERVAL')) : 1;
    if (!Number.isInteger(interval) || interval < 1 || interval > 366)
        return null;
    const rule = { freq: freq, interval };
    const byDay = parts.get('BYDAY');
    if (byDay) {
        const days = byDay.split(',').map((day) => day.trim());
        if (!days.every((day) => WEEKDAYS.includes(day)))
            return null;
        // Entdoppeln und in Wochenreihenfolge bringen: „SO,MO" und „MO,SO" sind
        // dieselbe Regel und muessen dieselbe naechste Faelligkeit ergeben.
        rule.byDay = WEEKDAYS.filter((day) => days.includes(day));
    }
    const byMonthDay = parts.get('BYMONTHDAY');
    if (byMonthDay) {
        const day = Number(byMonthDay);
        if (!Number.isInteger(day) || day === 0 || day < -1 || day > 31)
            return null;
        rule.byMonthDay = day;
    }
    return rule;
}
export function formatRrule(rule) {
    const parts = [`FREQ=${rule.freq}`];
    if (rule.interval > 1)
        parts.push(`INTERVAL=${rule.interval}`);
    if (rule.byDay?.length)
        parts.push(`BYDAY=${rule.byDay.join(',')}`);
    if (rule.byMonthDay !== undefined)
        parts.push(`BYMONTHDAY=${rule.byMonthDay}`);
    return parts.join(';');
}
/**
 * Wie viele Monate darf uebersprungen werden, bis aufgegeben wird?
 *
 * „Monatlich am 31." hat in einem Jahr sieben gueltige Termine — der Rest
 * wird nach RFC 5545 uebersprungen, nicht auf den 28. gezogen. Vier Jahre
 * Vorlauf sind grosszuegig genug fuer jede sinnvolle Regel und begrenzen
 * zugleich eine Schleife, die sonst bei einer kaputten Regel ewig liefe.
 */
const MAX_MONTH_STEPS = 48;
/**
 * Naechste Faelligkeit nach `from` — strikt danach, nie gleich.
 *
 * Die Tageszeit von `from` bleibt erhalten. `null` bedeutet: diese Regel hat
 * in absehbarer Zeit keinen Termin mehr (nur bei BYMONTHDAY moeglich).
 */
export function nextOccurrence(rule, from) {
    if (Number.isNaN(from.getTime()))
        return null;
    switch (rule.freq) {
        case 'DAILY':
            return addDays(from, rule.interval);
        case 'WEEKLY': {
            if (!rule.byDay?.length)
                return addDays(from, 7 * rule.interval);
            const targets = rule.byDay.map((day) => WEEKDAYS.indexOf(day)).sort((a, b) => a - b);
            const current = isoWeekday(from);
            // Noch ein Wochentag in derselben Woche uebrig?
            const later = targets.find((target) => target > current);
            if (later !== undefined)
                return addDays(from, later - current);
            /*
             * Sonst in die naechste zulaessige Woche. Der Sprung geht ueber den
             * Wochenanfang, nicht ueber „+7 Tage": bei INTERVAL=2 und BYDAY=MO,FR
             * soll nach dem Freitag der Montag der uebernaechsten Woche kommen,
             * nicht der Freitag plus zwei Wochen.
             */
            const weekStart = addDays(from, -current);
            const nextWeekStart = addDays(weekStart, 7 * rule.interval);
            return addDays(nextWeekStart, targets[0]);
        }
        case 'MONTHLY': {
            const wanted = rule.byMonthDay ?? from.getDate();
            for (let step = 1; step <= MAX_MONTH_STEPS; step += 1) {
                const target = new Date(from.getFullYear(), from.getMonth() + step * rule.interval, 1);
                const year = target.getFullYear();
                const month = target.getMonth();
                const length = daysInMonth(year, month);
                // -1 heisst ausdruecklich „letzter Tag des Monats" — der Weg, „Ende
                // des Monats" zu meinen, ohne am 31. im Februar zu scheitern.
                const day = wanted === -1 ? length : wanted;
                if (day > length)
                    continue; // RFC 5545: ungueltige Tage entfallen.
                return at(from, year, month, day);
            }
            return null;
        }
    }
}
/**
 * Naechster Verantwortlicher einer Rotation (SPEC 9.4).
 *
 * Loest das Muell-raus-Problem ohne Diskussion: die Liste ist die
 * Verabredung, der Index sagt, wer zuletzt dran war.
 */
export function nextInRotation(rotation, currentIndex) {
    if (rotation.length === 0)
        return null;
    // Modulo auch auf den Eingangswert: eine Liste, aus der jemand entfernt
    // wurde, hat sonst einen Index ausserhalb ihrer selbst.
    const index = (((currentIndex + 1) % rotation.length) + rotation.length) % rotation.length;
    return { userId: rotation[index], index };
}
//# sourceMappingURL=recurrence.js.map